import {h} from 'hastscript'

import {HastElementWidget} from './hast-element'
import styles from './upload-placeholder.module.css'

export interface UploadPlaceholderWidgetData {
  id: string
  fileName: string
  mimeType: string
  progressPercent?: number
  previewUrl?: string
  mediaKind?: 'image' | 'video'
}

export class UploadPlaceholderWidget extends HastElementWidget {
  readonly data: UploadPlaceholderWidgetData

  constructor(data: UploadPlaceholderWidgetData) {
    super(buildPlaceholder(data))
    this.data = data
  }

  override eq(other: UploadPlaceholderWidget) {
    const a = this.data
    const b = other.data
    return (
      a.fileName === b.fileName &&
      a.mediaKind === b.mediaKind &&
      a.progressPercent === b.progressPercent &&
      a.previewUrl === b.previewUrl
    )
  }
}

/** Build hast node tree for placeholder DOM. */
function buildPlaceholder(data: UploadPlaceholderWidgetData) {
  // Fallback: show file type label
  let preview = h('span', {className: styles.previewFallback}, fallbackLabel(data.fileName, data.mimeType))

  // If we have a preview URL (for images/videos), show the preview
  if (data.previewUrl && data.mediaKind) {
    if (data.mediaKind === 'video') {
      preview = h('video', {
        src: data.previewUrl,
        className: styles.previewVideo,
        muted: true,
        playsInline: true,
        preload: 'metadata',
        ariaHidden: 'true',
        style: 'max-width: 96px; max-height: 96px; height: auto;',
      })
    } else {
      preview = h('img', {
        src: data.previewUrl,
        alt: data.fileName,
        className: styles.previewImage,
        style: 'max-width: 96px; max-height: 96px; height: auto;',
      })
    }
  }

  const fileName = h('div', {className: styles.fileName}, data.fileName)
  const progress = h(
    'progress',
    {
      className: styles.progress,
      max: 100,
      'aria-label': 'Upload progress',
      value: typeof data.progressPercent === 'number' ? data.progressPercent : undefined,
    },
    typeof data.progressPercent === 'number' ? `${data.progressPercent}%` : '',
  )

  const container = h(
    'div',
    {
      className: styles.container,
      role: 'status',
      ariaLive: 'polite',
      ariaLabel: `Uploading ${data.fileName}`,
    },
    h('div', {className: styles.preview}, preview),
    h('div', {className: styles.body}, fileName, progress),
  )

  return container
}

function fallbackLabel(fileName: string, mimeType: string) {
  if (mimeType) {
    const [, subtype] = mimeType.split('/')
    if (subtype) return subtype.toUpperCase()
  }
  const parts = fileName.split('.')
  return (parts.length > 1 ? parts.at(-1) : undefined)?.toUpperCase() ?? 'FILE'
}
